/**
 * INDUSTRY_MARKETING_DATA
 * =======================
 * Iter-349 Part 12 (Frontend Perf) — extracted from `landingPageData.js`
 * to keep the parent under the 400 LOC frontend ratchet baseline.
 *
 * Marketing metadata (stakeholders, metrics, data sources) per industry ID.
 * Joined with the base industry list (`SUPPORTED_INDUSTRIES`) in the parent.
 */

export const INDUSTRY_MARKETING_DATA = {
  crm: {
    description: 'Sales teams, customer management',
    popular: true,
    featured: true,
    stakeholders: ['Sales Teams', 'Business Owners', 'Account Managers', 'Marketing Teams', 'Customer Success'],
    metrics: ['Deal pipeline', 'Lead conversion', 'Customer lifetime value', 'Sales velocity', 'Win rates'],
    dataSources: ['Email', 'Calendar', 'Phone', 'Social', 'Website analytics'],
    product: 'crm'
  },
  hospitality: {
    description: 'Restaurants, bars, cafes, venues',
    popular: true,
    stakeholders: ['Venue Operators', 'Investors', 'Landlords', 'Beverage Brands', 'Food Distributors', 'Franchise Groups'],
    metrics: ['Revenue per seat', 'EBITDA margins', 'Customer acquisition cost', 'Menu optimization', 'Staff performance'],
    dataSources: ['ABS', 'NIQ', 'OpenTable', 'SevenRooms', 'POS systems', 'TRA']
  },
  retail: {
    description: 'Stores, boutiques, online shops',
    popular: true,
    stakeholders: ['Store Operators', 'Investors', 'Landlords', 'Suppliers', 'Multi-location Chains', 'Shopping Centers'],
    metrics: ['Sales per sqm', 'Foot traffic', 'Conversion rates', 'Inventory turns', 'Online vs in-store'],
    dataSources: ['ABS Retail', 'Card transaction data', 'Foot traffic APIs', 'Market research', 'E-commerce platforms']
  },
  healthcare: {
    description: 'Clinics, hospitals, aged care',
    popular: true,
    stakeholders: ['Operators', 'Investors', 'Government', 'Equipment Suppliers', 'Facility Managers', 'Insurers'],
    metrics: ['Patient throughput', 'Bed occupancy', 'Staff ratios', 'Compliance scores', 'Cost per patient'],
    dataSources: ['AIHW', 'ACFA', 'Medicare data', 'Hospital benchmarks', 'Clinical systems']
  },
  professional: {
    description: 'Accounting, legal, consulting',
    popular: true,
    stakeholders: ['Firm Partners', 'Investors', 'Landlords', 'Software Vendors', 'Recruitment', 'Clients'],
    metrics: ['Utilization rates', 'Billing efficiency', 'Client retention', 'Revenue per employee', 'Win rates'],
    dataSources: ['ABS Professional Services', 'Practice management systems', 'LinkedIn data', 'Industry associations']
  },
  fitness: {
    description: 'Gyms, studios, wellness centers',
    popular: true,
    stakeholders: ['Gym Operators', 'Investors', 'Landlords', 'Equipment Suppliers', 'Franchise Groups', 'Personal Trainers'],
    metrics: ['Member retention', 'Revenue per member', 'Class utilization', 'Equipment ROI', 'Acquisition cost'],
    dataSources: ['ABS Recreation', 'Membership systems', 'Booking platforms', 'Market research', 'Competitor data']
  },
  property: {
    description: 'Property management, agencies',
    popular: true,
    stakeholders: ['Landlords', 'Investors', 'Tenants', 'Developers', 'Brokers', 'Property Managers'],
    metrics: ['Lease performance', 'Tenant health', 'Rent benchmarks', 'Vacancy rates', 'Market trends'],
    dataSources: ['ABS Housing', 'CoreLogic', 'Domain', 'Tenant financials', 'Market reports']
  },
  construction: {
    description: 'Builders, contractors, construction',
    popular: true,
    stakeholders: ['Builders', 'Project Managers', 'Subcontractors', 'Suppliers', 'Developers', 'Investors'],
    metrics: ['Project margin', 'On-time delivery', 'Safety rate', 'Variation rate', 'Defect rate'],
    dataSources: ['MBA', 'HIA', 'ABCB', 'Project systems', 'Safety reports']
  },
  automotive: {
    description: 'Dealerships, workshops, service',
    popular: true,
    stakeholders: ['Dealers', 'Workshop Owners', 'Fleet Managers', 'Parts Suppliers', 'Insurance', 'Franchise Groups'],
    metrics: ['Units sold', 'Service absorption', 'Parts margin', 'CSI scores', 'Labour efficiency'],
    dataSources: ['FCAI', 'MTA', 'Workshop systems', 'DMS platforms', 'Industry benchmarks']
  },
  education: {
    description: 'Schools, training, childcare',
    popular: true,
    stakeholders: ['School Principals', 'RTOs', 'Tutoring Centres', 'Childcare', 'Training Providers'],
    metrics: ['Enrolment numbers', 'Completion rate', 'Satisfaction', 'Attendance', 'Fee collection'],
    dataSources: ['Department of Education', 'ASQA', 'ACECQA', 'Student systems', 'Compliance data']
  },
  beauty: {
    description: 'Salons, barbershops, clinics',
    popular: true,
    stakeholders: ['Salon Owners', 'Therapists', 'Product Suppliers', 'Franchise Groups', 'Stylists'],
    metrics: ['Client retention', 'Revenue per service', 'Chair utilization', 'Retail ratio', 'Rebooking rate'],
    dataSources: ['AHC', 'Booking systems', 'Salon software', 'Product suppliers', 'Review platforms']
  },
  manufacturing: {
    description: 'Production, warehousing, factories',
    popular: true,
    stakeholders: ['Factory Owners', 'Plant Managers', 'Quality Managers', 'Supply Chain', 'Maintenance'],
    metrics: ['OEE', 'First pass yield', 'On-time delivery', 'Downtime', 'Inventory turns'],
    dataSources: ['AI Group', 'ABS Manufacturing', 'ERP systems', 'Quality systems', 'Supply chain data']
  },
  entertainment: {
    description: 'Venues, cinemas, tourism, events',
    popular: false,
    stakeholders: ['Event Managers', 'Tour Operators', 'Venue Owners', 'Talent', 'Sponsors', 'Marketing'],
    metrics: ['Ticket sales', 'Occupancy', 'Revenue per guest', 'Direct bookings', 'Review scores'],
    dataSources: ['TRA', 'Ticketing systems', 'OTA platforms', 'Review sites', 'Event analytics']
  },
  technology: {
    description: 'IT, SaaS, tech startups',
    popular: true,
    stakeholders: ['CEOs', 'CTOs', 'Product Managers', 'Developers', 'Sales Teams', 'Customer Success'],
    metrics: ['MRR', 'Churn rate', 'CAC', 'LTV:CAC ratio', 'NPS', 'Activation rate'],
    dataSources: ['Stripe', 'Analytics', 'Support systems', 'GitHub', 'Customer feedback']
  },
  finance: {
    description: 'Banks, fintech, investments',
    popular: true,
    stakeholders: ['Directors', 'Compliance Officers', 'Portfolio Managers', 'Advisors', 'Analysts'],
    metrics: ['AUM', 'Client retention', 'Revenue per client', 'Compliance score', 'Referral rate'],
    dataSources: ['ASIC', 'APRA', 'Portfolio systems', 'Compliance data', 'Client systems']
  },
  agriculture: {
    description: 'Farms, agribusiness, livestock',
    popular: false,
    stakeholders: ['Farm Owners', 'Agronomists', 'Livestock Managers', 'Equipment Suppliers', 'Distributors'],
    metrics: ['Yield per hectare', 'Weight gain rate', 'Water efficiency', 'Cost per hectare', 'Gross margin'],
    dataSources: ['ABS Agriculture', 'AgWorld', 'Weather services', 'Market prices', 'Compliance data']
  },
  logistics: {
    description: 'Freight, warehousing, distribution',
    popular: false,
    stakeholders: ['Fleet Owners', 'Warehouse Managers', 'Dispatch Coordinators', 'Drivers', 'Clients'],
    metrics: ['On-time delivery', 'Cost per delivery', 'Fleet utilization', 'Pick accuracy', 'Fuel efficiency'],
    dataSources: ['TMS systems', 'GPS tracking', 'WMS systems', 'Fuel cards', 'Customer portals']
  },
  trades: {
    description: 'Plumbers, electricians, HVAC',
    popular: false,
    stakeholders: ['Business Owners', 'Tradespeople', 'Apprentices', 'Office Staff', 'Customers'],
    metrics: ['Jobs completed', 'First-time fix rate', 'Average job value', 'Response time', 'Customer satisfaction'],
    dataSources: ['ServiceM8', 'Tradify', 'Job management systems', 'Google reviews', 'Scheduling apps']
  },
  legal: {
    description: 'Law firms, barristers, conveyancers',
    popular: false,
    stakeholders: ['Managing Partners', 'Partners', 'Associates', 'Paralegals', 'Legal Secretaries'],
    metrics: ['Billable hours', 'Realization rate', 'Collection rate', 'Matter profitability', 'WIP days'],
    dataSources: ['LEAP', 'Smokeball', 'Clio', 'Trust accounting', 'Legal research platforms']
  },
  accommodation: {
    description: 'Hotels, motels, Airbnb, resorts',
    popular: false,
    stakeholders: ['Hotel Owners', 'General Managers', 'Revenue Managers', 'Housekeeping', 'Front Office'],
    metrics: ['Occupancy rate', 'ADR', 'RevPAR', 'Guest satisfaction', 'Direct bookings'],
    dataSources: ['PMS systems', 'Channel managers', 'OTA platforms', 'Review sites', 'Revenue systems']
  },
  gaming: {
    description: 'Gaming venues, esports, arcades',
    popular: false,
    stakeholders: ['Venue Owners', 'Esports Managers', 'Technical Staff', 'Event Coordinators', 'Members'],
    metrics: ['Station utilization', 'Revenue per station', 'Tournament attendance', 'Member retention', 'F&B attach'],
    dataSources: ['Venue management systems', 'Tournament platforms', 'POS systems', 'Membership systems', 'Streaming analytics']
  },
  environmental: {
    description: 'Renewable energy, waste, consulting',
    popular: false,
    stakeholders: ['Directors', 'Consultants', 'Project Managers', 'Environmental Officers', 'Field Technicians'],
    metrics: ['Carbon offset', 'Waste diversion', 'Energy savings', 'Compliance rate', 'Project margin'],
    dataSources: ['Carbon tracking systems', 'EPA data', 'IoT sensors', 'Project management', 'Sustainability reporting']
  },
  cleaning: {
    description: 'Commercial, residential, specialty cleaning',
    popular: false,
    stakeholders: ['Franchise Owners', 'Operations Managers', 'Cleaners', 'Commercial Clients', 'Property Managers'],
    metrics: ['Jobs completed', 'Client retention', 'Revenue per job', 'Staff productivity', 'Quality scores'],
    dataSources: ['Job management systems', 'CRM', 'Scheduling platforms', 'Client feedback', 'Time tracking']
  },
  pet_services: {
    description: 'Grooming, boarding, daycare, vet clinics',
    popular: false,
    stakeholders: ['Pet Business Owners', 'Groomers', 'Pet Sitters', 'Veterinary Partners', 'Pet Parents'],
    metrics: ['Bookings', 'Client retention', 'Revenue per pet', 'Service utilization', 'Rebooking rate'],
    dataSources: ['Booking systems', 'Client databases', 'POS systems', 'Veterinary records', 'Review platforms']
  },
  childcare: {
    description: 'Daycare, preschools, after school',
    popular: false,
    stakeholders: ['Center Directors', 'Educators', 'Parents', 'Compliance Officers', 'Franchise Groups'],
    metrics: ['Enrolment', 'Occupancy rate', 'Staff ratios', 'Compliance scores', 'Parent satisfaction'],
    dataSources: ['ACECQA', 'Childcare management systems', 'Parent portals', 'Compliance data', 'CCS data']
  },
  senior_care: {
    description: 'Aged care, NDIS, home health',
    popular: false,
    stakeholders: ['Care Providers', 'Care Coordinators', 'Families', 'NDIS Participants', 'Healthcare Partners'],
    metrics: ['Client hours', 'Care quality', 'NDIS utilization', 'Staff retention', 'Client satisfaction'],
    dataSources: ['Care management systems', 'NDIS portal', 'Rostering systems', 'Client assessments', 'Healthcare data']
  },
  pharmacy: {
    description: 'Retail pharmacy, compounding',
    popular: false,
    stakeholders: ['Pharmacy Owners', 'Pharmacists', 'Technicians', 'Healthcare Providers', 'Patients'],
    metrics: ['Scripts dispensed', 'Revenue per script', 'Inventory turns', 'Compliance rate', 'PBS claiming'],
    dataSources: ['Dispensing systems', 'PBS data', 'Inventory management', 'Patient records', 'Wholesaler data']
  },
  staffing: {
    description: 'Recruitment agencies, temp staffing',
    popular: false,
    stakeholders: ['Agency Owners', 'Recruiters', 'Candidates', 'Client Companies', 'HR Managers'],
    metrics: ['Placements', 'Fill rate', 'Time to fill', 'Margin', 'Client retention', 'Candidate NPS'],
    dataSources: ['ATS systems', 'Job boards', 'LinkedIn', 'CRM', 'Timesheet systems']
  },
  printing: {
    description: 'Print shops, signage, wide format',
    popular: false,
    stakeholders: ['Print Shop Owners', 'Production Managers', 'Designers', 'Sales Reps', 'Commercial Clients'],
    metrics: ['Jobs completed', 'Revenue per job', 'Production efficiency', 'Waste rate', 'Repeat customers'],
    dataSources: ['Print MIS systems', 'CRM', 'Production tracking', 'Inventory systems', 'Client feedback']
  },
  pest_control: {
    description: 'Commercial, residential pest services',
    popular: false,
    stakeholders: ['Business Owners', 'Technicians', 'Commercial Clients', 'Residential Customers', 'Property Managers'],
    metrics: ['Jobs completed', 'First-time fix rate', 'Revenue per job', 'Customer retention', 'Response time'],
    dataSources: ['Field service systems', 'CRM', 'Route optimization', 'Customer feedback', 'Compliance tracking']
  },
  moving: {
    description: 'Moving companies, storage facilities',
    popular: false,
    stakeholders: ['Business Owners', 'Move Coordinators', 'Drivers', 'Packers', 'Storage Clients'],
    metrics: ['Moves completed', 'Revenue per move', 'Damage rate', 'Customer satisfaction', 'Storage occupancy'],
    dataSources: ['Move management systems', 'CRM', 'GPS tracking', 'Customer reviews', 'Storage management']
  },
};
